import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  TrendingUp,
  Target,
  Percent,
  AlertTriangle,
  CalendarDays,
  Clock,
  ArrowUpRight,
} from 'lucide-react';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import {
  useEtapas,
  useIndicadorConversao,
  useIndicadorFechamentoVencido,
  useIndicadorForecastMensal,
  useIndicadorPipeline,
  useIndicadorPorEtapa,
  useIndicadorSemProximaAcao,
  useIndicadorStalled,
} from '../hooks';
import { formatCurrencyBRL, formatDateBR } from '../utils/format';

/**
 * Dashboard mínimo de indicadores (US-COM-004 §Indicadores Comerciais,
 * task 16.2). Foca nos 5 KPIs prioritários da task:
 *
 *   1. Pipeline aberto (count + valor total)
 *   2. Valor ponderado do pipeline
 *   3. Forecast mês atual (soma ponderada por default)
 *   4. Taxa de conversão global
 *   5. Oportunidades paradas (count)
 *
 * Complementa com barra por etapa (visão rápida do funil) e listas
 * curtas de "sem próxima ação" e "fechamento vencido" para drill-down.
 * O usuário clica em qualquer card/linha e vai para a listagem filtrada
 * ou para o detalhe.
 *
 * Sem lib de gráfico — barras CSS puras cobrem o MVP; instalar
 * Recharts/Chart.js pode entrar em fatia dedicada de visualização.
 */
export default function OportunidadesIndicadoresPage(): React.ReactElement {
  const navigate = useNavigate();

  const monthRange = useMemo(() => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    return { from: iso(from), to: iso(to) };
  }, []);

  // Período de referência da conversão: últimos 12 meses.
  const yearRange = useMemo(() => {
    const now = new Date();
    const from = new Date(now.getFullYear() - 1, now.getMonth(), 1);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    return { from: iso(from), to: iso(now) };
  }, []);

  const pipeline = useIndicadorPipeline();
  const forecast = useIndicadorForecastMensal({ ...monthRange, weighted: true });
  const conversao = useIndicadorConversao(yearRange);
  const stalled = useIndicadorStalled();
  const porEtapa = useIndicadorPorEtapa();
  const semAcao = useIndicadorSemProximaAcao();
  const vencido = useIndicadorFechamentoVencido();
  const { data: stages } = useEtapas(true);

  const stageLabel = useMemo(() => {
    const m = new Map<string, string>();
    (stages ?? []).forEach((s) => m.set(s.id, s.label));
    return m;
  }, [stages]);

  // Soma do bucket do mês atual (o backend devolve 1 linha só quando
  // filtramos por from/to do mês) — pode não vir se não houver dados.
  const forecastMes = forecast.data?.buckets[0]?.total ?? null;

  // Barra por etapa: máximo do conjunto para normalizar largura visual.
  const stageMax = useMemo(() => {
    return (porEtapa.data ?? []).reduce((max, s) => Math.max(max, Number(s.total || 0)), 0);
  }, [porEtapa.data]);

  const openError =
    pipeline.error ||
    forecast.error ||
    conversao.error ||
    stalled.error ||
    porEtapa.error ||
    semAcao.error ||
    vencido.error;

  return (
    <div className="p-6 space-y-6">
      <button
        onClick={() => navigate('/oportunidades')}
        className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar para a lista
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Indicadores</h1>
        <p className="mt-1 text-sm text-gray-600">
          Visão executiva do pipeline. Os totais respeitam confidencialidade —
          oportunidades fora do seu allow-list não entram nas somas.
        </p>
      </div>

      {openError && (
        <Alert variant="warning" className="mb-4">
          Alguns indicadores falharam ao carregar. Recarregue a página para tentar
          novamente.
        </Alert>
      )}

      {/* 5 KPIs prioritários */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard
          icon={<Target className="h-5 w-5" />}
          label="Pipeline aberto"
          loading={pipeline.isLoading}
          value={formatCurrencyBRL(pipeline.data?.total ?? null)}
          hint={
            pipeline.data
              ? `${pipeline.data.count} oportunidade${pipeline.data.count === 1 ? '' : 's'}`
              : undefined
          }
        />
        <KpiCard
          icon={<TrendingUp className="h-5 w-5" />}
          label="Valor ponderado"
          loading={pipeline.isLoading}
          value={formatCurrencyBRL(pipeline.data?.weighted ?? null)}
          hint="Valor × probabilidade"
        />
        <KpiCard
          icon={<CalendarDays className="h-5 w-5" />}
          label="Forecast do mês"
          loading={forecast.isLoading}
          value={formatCurrencyBRL(forecastMes)}
          hint="Ponderado, expected_closing"
        />
        <KpiCard
          icon={<Percent className="h-5 w-5" />}
          label="Taxa de conversão"
          loading={conversao.isLoading}
          value={conversao.data ? `${(conversao.data.globalRate * 100).toFixed(1)}%` : '—'}
          hint={
            conversao.data
              ? `${conversao.data.totals.won} ganhas / ${
                  conversao.data.totals.won +
                  conversao.data.totals.lost +
                  conversao.data.totals.cancelled
                } encerradas (12m)`
              : undefined
          }
        />
        <KpiCard
          icon={<AlertTriangle className="h-5 w-5" />}
          label="Paradas"
          loading={stalled.isLoading}
          value={stalled.data ? String(stalled.data.length) : '—'}
          hint="Sem movimentação além do prazo"
          alert={!!stalled.data?.length}
          onClick={
            stalled.data?.length
              ? () => navigate('/oportunidades?filter=stalled')
              : undefined
          }
        />
      </div>

      {/* Barra por etapa */}
      <Section title="Pipeline por etapa" description="Valor estimado por etapa aberta.">
        {porEtapa.isLoading && <Skeleton className="h-40 w-full" />}
        {!porEtapa.isLoading && (porEtapa.data ?? []).length === 0 && (
          <p className="text-sm text-gray-500">Sem oportunidades abertas no pipeline.</p>
        )}
        {!porEtapa.isLoading && (porEtapa.data ?? []).length > 0 && (
          <div className="space-y-2">
            {(porEtapa.data ?? [])
              .slice()
              .sort((a, b) => Number(b.total) - Number(a.total))
              .map((row) => {
                const label = stageLabel.get(row.stageId) ?? row.stageId;
                const pct = stageMax > 0 ? Math.max(2, (Number(row.total) / stageMax) * 100) : 0;
                return (
                  <div key={row.stageId} className="flex items-center gap-3 text-sm">
                    <div className="w-32 truncate text-gray-700">{label}</div>
                    <div className="relative h-4 flex-1 rounded bg-gray-100">
                      <div
                        className="h-full rounded bg-blue-500"
                        style={{ width: `${pct}%` }}
                        aria-hidden
                      />
                    </div>
                    <div className="w-24 shrink-0 text-right tabular-nums text-gray-700">
                      {formatCurrencyBRL(row.total)}
                    </div>
                    <div className="w-16 shrink-0 text-right text-xs text-gray-500">
                      {row.count} opp
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </Section>

      {/* Drill-down de saúde */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <DrillDownList
          title="Sem próxima ação"
          description="Oportunidades abertas sem `next_action_date` — sinal de abandono."
          loading={semAcao.isLoading}
          items={(semAcao.data ?? []).slice(0, 5)}
          totalCount={semAcao.data?.length ?? 0}
          onClickItem={(id) => navigate(`/oportunidades/${id}`)}
        />
        <DrillDownList
          title="Fechamento vencido"
          description="Data prevista já passou — precisa justificar ou remarcar."
          loading={vencido.isLoading}
          items={(vencido.data ?? []).slice(0, 5)}
          totalCount={vencido.data?.length ?? 0}
          onClickItem={(id) => navigate(`/oportunidades/${id}`)}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------

const Section: React.FC<React.PropsWithChildren<{ title: string; description?: string }>> = ({
  title,
  description,
  children,
}) => (
  <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
    <div className="mb-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
        {title}
      </h2>
      {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
    </div>
    {children}
  </section>
);

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  loading?: boolean;
  alert?: boolean;
  onClick?: () => void;
}
const KpiCard: React.FC<KpiCardProps> = ({ icon, label, value, hint, loading, alert, onClick }) => {
  const clickable = !!onClick;
  return (
    <div
      onClick={onClick}
      className={`rounded-lg border p-4 shadow-sm ${
        alert ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'
      } ${clickable ? 'cursor-pointer hover:shadow' : ''}`}
    >
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span className={alert ? 'text-amber-700' : 'text-gray-400'} aria-hidden>
          {icon}
        </span>
        {label}
      </div>
      {loading ? (
        <Skeleton className="mt-2 h-7 w-2/3" />
      ) : (
        <div
          className={`mt-1 text-2xl font-semibold tabular-nums ${
            alert ? 'text-amber-800' : 'text-gray-900'
          }`}
        >
          {value}
        </div>
      )}
      {hint && (
        <div className={`text-xs ${alert ? 'text-amber-700' : 'text-gray-500'}`}>{hint}</div>
      )}
    </div>
  );
};

interface DrillDownItem {
  id: string;
  codigo: string | null;
  nome: string;
  expectedClosingDate: string | null;
  valorEstimado: string | null;
}
interface DrillDownListProps {
  title: string;
  description: string;
  loading?: boolean;
  items: DrillDownItem[];
  totalCount: number;
  onClickItem: (id: string) => void;
}
const DrillDownList: React.FC<DrillDownListProps> = ({
  title,
  description,
  loading,
  items,
  totalCount,
  onClickItem,
}) => (
  <Section title={title} description={description}>
    {loading && (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    )}
    {!loading && items.length === 0 && (
      <p className="text-sm text-gray-500 flex items-center gap-2">
        <Clock className="h-4 w-4 text-gray-400" />
        Nenhum item — pipeline saudável neste critério.
      </p>
    )}
    {!loading && items.length > 0 && (
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it.id}>
            <button
              type="button"
              onClick={() => onClickItem(it.id)}
              className="flex w-full items-center justify-between gap-2 rounded p-2 text-left text-sm hover:bg-gray-50"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-gray-800">{it.nome}</div>
                <div className="text-xs text-gray-500">
                  {it.codigo ?? '—'} • Fechamento previsto {formatDateBR(it.expectedClosingDate)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs tabular-nums text-gray-700">
                  {formatCurrencyBRL(it.valorEstimado)}
                </div>
                <ArrowUpRight className="ml-auto h-3 w-3 text-gray-400" />
              </div>
            </button>
          </li>
        ))}
        {totalCount > items.length && (
          <li className="pt-2 text-center text-xs text-gray-500">
            +{totalCount - items.length} outra{totalCount - items.length === 1 ? '' : 's'}
          </li>
        )}
      </ul>
    )}
  </Section>
);
